// lint-internal-leaks-ignore-next-line: framework-script — load-bearing visual regression marker
// @visual-diff-render: vite
import React, { useEffect, useState, useRef } from 'react';
import type { ReactNode } from 'react';
import { AssessmentStep } from '../AssessmentStep';
import { AssessmentQuestionEyebrow } from '../../ui/AssessmentQuestionEyebrow';
import { AssessmentScale, type AssessmentScaleItem } from '../../ui/AssessmentScale';
import type { AssessmentRank } from '../../ui/AssessmentCard';
import { Button, ButtonArrow, ButtonSpinner } from '../../ui/Button';
import { ErrorMessage } from '../../ui/ErrorMessage';
import { Toggle } from '../../ui/Toggle';
import { FormControlLabel } from '@mui/material';

/*
 * AssessmentQuestion — single in-flow question screen of the Business Impact
 * Inventory assessment.
 *
 * Composes the AssessmentStep template chrome (Navbar / Background / Footer)
 * with question-specific slot content: AssessmentQuestionEyebrow above the
 * h1 question title, prompt body, the 5-card AssessmentScale picker, and a
 * Back / Next Question action row. Tracks the picker selection in local
 * useState — `onAnswered` fires when the user picks a rank and clicks Next.
 *
 *   <AssessmentQuestion
 *     brand="mayo"
 *     assessmentName="Business Impact Inventory"
 *     percent={50}
 *     category="Organizational Impact"
 *     questionNumber={1}
 *     totalQuestions={12}
 *     title="Investment Leadership"
 *     prompt="Identified and drove new, large-scale investments…"
 *     items={SCALE_ITEMS}
 *     onBack={() => …}
 *     onAnswered={(rank) => …}
 *   />
 *
 * State is self-contained at the screen level; a host flow controller wires
 * onBack / onAnswered to advance through the question sequence.
 */

const SecondaryOnDark = (props: React.ComponentProps<typeof Button>) => (
  <Button
    {...props}
    variant="secondary"
    sx={{
      color: '#FFFFFF',
      border: '1px solid rgba(255, 255, 255, 0.85)',
      '&:hover, &:active, &[data-force-hover]': {
        border: '1.5px solid #FFFFFF',
      },
      ...(props.sx as object),
    }}
  />
);

export interface AssessmentQuestionProps {
  /** Tenant brand — forwarded to the AssessmentNav chrome. */
  brand: 'mayo' | 'hs';
  /** Assessment title text — forwarded to AssessmentNav. */
  assessmentName: string;
  /** Completion percent (0–100) — forwarded to AssessmentNav. */
  percent: number;
  /** Section / category name shown left of the rule in the eyebrow. */
  category: string;
  /** Current question's position in the flow (1-indexed). */
  questionNumber: number;
  /** Total questions in the flow. */
  totalQuestions: number;
  /** Question heading (rendered as h1). */
  title: string;
  /** Section title (rendered below the eyebrow). */
  section?: string;
  /** Whether auto-advance is on. Controlled by the parent flow so the choice
   *  persists across questions until the user turns it off. */
  autoAdvance?: boolean;
  /** Fires when the user toggles auto-advance. */
  onAutoAdvanceChange?: (next: boolean) => void;
  /** Prompt body below the question heading. */
  prompt: ReactNode;
  /** The 5 scale items the user picks from. */
  items: readonly AssessmentScaleItem[];
  /** Optional initial pick — useful when navigating back to a previously-answered question. */
  initialSelected?: AssessmentRank;
  /** Validation message shown when the user advances without picking an answer. */
  errorMessage?: string;
  /** Fires when the user clicks Back. */
  onBack?: () => void;
  /** Fires when the user clicks Next Question with a rank picked. */
  onAnswered?: (rank: AssessmentRank) => void;
  /** Shows a spinner on the Continue button and disables both action buttons while the parent is submitting. */
  submitting?: boolean;
  /** Render only the content well (no nav/background/footer chrome). Used by a
   *  question flow that renders chrome once and overlaps two questions during a
   *  transition. Default `true`. */
  chrome?: boolean;
  /** Question-transition phase (rest | enter | exit), forwarded to the template. */
  phase?: 'rest' | 'enter' | 'exit';
  /** Transition direction (1 = next, -1 = previous), forwarded to the template. */
  direction?: 1 | -1;
  /**
   * Colour treatment. `dark` (default) is the white-chrome-on-dark flow; `light`
   * flips the step text, eyebrow, scale cards, and the Back/Continue buttons to
   * their light-surface treatments (navy outline + navy-filled). Per-instance variant.
   */
  theme?: 'light' | 'dark';
}

export const AssessmentQuestion: React.FC<AssessmentQuestionProps> = ({
  brand,
  assessmentName,
  percent,
  category,
  questionNumber,
  totalQuestions,
  section,
  autoAdvance = false,
  onAutoAdvanceChange,
  title,
  prompt,
  items,
  initialSelected,
  errorMessage = 'Please select one of the options before continuing.',
  onBack,
  onAnswered,
  submitting = false,
  chrome,
  phase,
  direction,
  theme = 'dark',
}) => {
  const [selected, setSelected] = useState<AssessmentRank | undefined>(initialSelected);
  const [showError, setShowError] = useState(false);
  const isLight = theme === 'light';
  // On light surfaces the Back / de-emphasised Continue are a plain navy
  // secondary (navy outline + navy text); on dark they're the white-outline
  // SecondaryOnDark. The primary Continue is navy-filled (tone="dark") on light,
  // bright cyan on dark.
  const Secondary = isLight
    ? (props: React.ComponentProps<typeof Button>) => <Button variant="secondary" {...props} />
    : SecondaryOnDark;

  const onToggleAutoAdvance = (e: React.ChangeEvent<HTMLInputElement>) => {
    onAutoAdvanceChange?.(e.target.checked);
  };

  // Picking an answer clears any standing validation error.
  const handleSelect = (rank: AssessmentRank) => {
    // Mark this selection as a user action so auto-advance only runs
    // for explicit user selections (not for initialSelected prop sync).
    userSelectedRef.current = true;
    setSelected(rank);
    setShowError(false);
  };

  // Track whether the most-recent selection was a user action. When the
  // component receives `initialSelected` from the parent (e.g. when
  // navigating back), we sync `selected` from props but do NOT treat that as a
  // fresh user pick. Only user-initiated picks should trigger auto-advance.
  const userSelectedRef = useRef(false);

  // Sync `initialSelected` when the parent changes it (navigation/back).
  useEffect(() => {
    setSelected(initialSelected);
    // Clear any pending user-selection marker when initial selection is applied.
    userSelectedRef.current = false;
  }, [initialSelected]);

  // Auto-advance only when the user explicitly picked a value.
  useEffect(() => {
    if (autoAdvance && selected !== undefined && userSelectedRef.current) {
      userSelectedRef.current = false;
      onAnswered?.(selected);
    }
  }, [selected, autoAdvance, onAnswered]);

  // The Next button is always enabled; advancing without a pick surfaces the
  // error instead of being blocked by a disabled state.
  const handleNext = () => {
    if (selected === undefined) {
      setShowError(true);
      return;
    }
    onAnswered?.(selected);
  };

  return (
    <AssessmentStep
      template="question"
      chrome={chrome}
      phase={phase}
      direction={direction}
      theme={theme}
      brand={brand}
      assessmentName={assessmentName}
      percent={percent}
      eyebrow={
        <AssessmentQuestionEyebrow
          label={category}
          questionNumber={questionNumber}
          totalQuestions={totalQuestions}
          skillSection={section}
          theme={theme}
        />
      }
      title={title}
      body={prompt}
      actions={
        <>
          <Secondary size="md" onClick={onBack} disabled={submitting}>
            Previous
          </Secondary>
          {/* When auto-advance is on, picking an answer moves the flow forward on
           *  its own, so Continue is a fallback — de-emphasized to match the
           *  Previous button's secondary treatment. Off, it's the primary CTA
           *  (bright cyan on dark, navy-filled on light). */}
          {autoAdvance ? (
            <Secondary size="md" endIcon={submitting ? <ButtonSpinner /> : <ButtonArrow />} onClick={handleNext} disabled={submitting}>
              Continue
            </Secondary>
          ) : (
            <Button
              variant="primary"
              size="md"
              tone={isLight ? 'dark' : 'bright'}
              endIcon={submitting ? <ButtonSpinner /> : <ButtonArrow />}
              onClick={handleNext}
              disabled={submitting}
            >
              Continue
            </Button>
          )}
        </>
      }
      belowActions={
        /* The error OVERLAYS just below the actions (absolute, top: 100% of the
         * main block) so it reserves no flow space at rest — no band below the
         * buttons — and showing/hiding it never shifts the layout. */
         <>
          <div
            style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              paddingTop: 16,
              display: 'flex',
              justifyContent: 'center',
              pointerEvents: 'none',
            }}
          >
            {showError && <ErrorMessage>{errorMessage}</ErrorMessage>}
          </div>

          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <FormControlLabel
              control={
                <Toggle
                  size="small"
                  accentColor="var(--hd-raw-color-teal-accessible)"
                  checked={autoAdvance}
                  onChange={onToggleAutoAdvance}
                />
              }
              label={
                <span style={{
                  marginLeft: 'var(--hd-raw-space-12)'
                }}>
                  Auto-advance to the next question
                </span>
              }
            />
          </div>
        </>
      }
    >
      <AssessmentScale items={items} selected={selected} onSelect={handleSelect} theme={theme} />
    </AssessmentStep>
  );
};

AssessmentQuestion.displayName = 'AssessmentQuestion';
